import { useState, useEffect, useRef, useCallback } from "react";
import { AdminCard, AdminCardHead } from "components/admin/ui";
import { adminGet, adminPostForm, adminPatch, adminDelete } from "lib/adminClient";
import InstitutionTags from "components/admin/InstitutionTags";
import { mediaUrl } from "lib/media";

const BASE_CATEGORIES = ["Event", "Science", "Sport", "Culture", "Campus"];

export default function GalleryPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("Event");
  const [tags, setTags] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setPhotos(await adminGet("/admin/gallery"));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    if (!label) setLabel(f.name.replace(/\.[^.]+$/, "")); // default label = filename
  };

  const upload = async () => {
    if (!file || !label.trim() || uploading) return;
    setUploading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("label", label.trim());
      form.append("category", category);
      if (tags.length) form.append("tags", tags.join(","));
      await adminPostForm("/admin/gallery", form);
      setFile(null);
      setLabel("");
      setTags([]);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  // Metadata-only update: the PATCH route never touches imageUrl, so retagging
  // an existing photo cannot lose its picture.
  const retag = async (id, next) => {
    setPhotos((cur) => cur.map((p) => (p.id === id ? { ...p, tags: next } : p)));
    try {
      await adminPatch(`/admin/gallery/${id}`, { tags: next });
    } catch (e) {
      setError(e.message);
      load();
    }
  };

  const del = async (id) => {
    try {
      await adminDelete(`/admin/gallery/${id}`);
      setPhotos((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="animate-fadeIn">
      <AdminCard className="p-[22px] mb-5">
        <div className="font-bold text-[15px] text-green-dark mb-3.5">📤 Upload a Photo</div>

        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            pickFile(e.dataTransfer.files?.[0]);
          }}
          className="border-2 border-dashed border-[#cdd4d9] bg-[#fafbfc] rounded-xl p-7 text-center cursor-pointer hover:border-green transition-colors mb-4"
        >
          <div className="text-[32px] mb-1.5">🖼</div>
          <div className="text-[13px] text-muted">
            {file ? (
              <span className="font-semibold text-green-dark">{file.name}</span>
            ) : (
              <>Drag &amp; drop an image here, or click to browse · JPG/PNG/WebP up to 10MB</>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
        </div>

        <div className="mb-3">
          <InstitutionTags value={tags} onChange={setTags} label="Show on" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Caption / label"
            className="px-3.5 py-[11px] border-[1.5px] border-line rounded-[9px] font-sans text-sm outline-none focus:border-green"
          />
          <input
            list="gallery-categories"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Category (pick or type a new one)"
            className="px-3.5 py-[11px] border-[1.5px] border-line rounded-[9px] font-sans text-sm outline-none focus:border-green bg-white"
          />
          <datalist id="gallery-categories">
            {Array.from(new Set([...BASE_CATEGORIES, ...photos.map((p) => p.category).filter(Boolean)])).map(
              (c) => (
                <option key={c} value={c} />
              )
            )}
          </datalist>
          <button
            onClick={upload}
            disabled={!file || !label.trim() || uploading}
            className="bg-green text-white border-none rounded-[9px] px-7 font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </AdminCard>

      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      <AdminCard>
        <AdminCardHead title={`Gallery Photos (${photos.length})`} />
        {loading ? (
          <div className="px-[22px] py-8 text-center text-muted text-sm">Loading…</div>
        ) : photos.length === 0 ? (
          <div className="px-[22px] py-8 text-center text-muted text-sm">No photos yet.</div>
        ) : (
          <div className="p-[22px] grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3.5">
            {photos.map((p) => (
              <div key={p.id} className="flex flex-col gap-2">
              <div
                className="rounded-xl overflow-hidden relative h-[130px] bg-cover bg-center"
                style={{ background: p.imageUrl ? undefined : p.color }}
              >
                {p.imageUrl ? (
                  <img
                    src={mediaUrl(p.imageUrl)}
                    alt={p.label}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[28px] opacity-40">
                    📷
                  </div>
                )}
                <div className="absolute bottom-0 inset-x-0 px-3 pt-5 pb-2.5 bg-[linear-gradient(to_top,rgba(0,0,0,0.7),transparent)]">
                  <div className="text-[10px] text-white/65 font-bold uppercase">{p.category}</div>
                  <div className="text-[11.5px] text-white font-semibold leading-[1.3]">{p.label}</div>
                </div>
                <button
                  onClick={() => del(p.id)}
                  className="absolute top-2 right-2 bg-black/50 border-none text-white w-[26px] h-[26px] rounded-[7px] cursor-pointer text-xs"
                >
                  🗑
                </button>
              </div>
              <InstitutionTags
                value={p.tags || []}
                onChange={(next) => retag(p.id, next)}
              />
              </div>
            ))}
          </div>
        )}
      </AdminCard>
    </div>
  );
}
